import { useState } from "react";
import { Box, CloseButton, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import { AlertTriangle } from "lucide-react";
import type { FloorAlarmRow } from "@/lib/api/client";
import { preAlarmWarningText, preAlarmWarningTime, preAlarmWarnings, warningKey } from "@/lib/floor-alarms";
import { useNetworkStatus } from "@/context/network-context";
import { useFloorAlarms } from "@/hooks/use-floor-alarms";


interface FloorAlarmBannerViewProps {
  rows: FloorAlarmRow[] | undefined;
  networkStatus: string;
  dismissed?: ReadonlySet<string>;
  onDismiss?: (key: string) => void;
}

/**
 * The pre-alarm warning at the top of every portal page: the alarm on a floor
 * the member can see turns on in a few minutes (`warning_minutes`, or "soon" when the
 * pre-alarm source has none set). Only while the member is on
 * site (the network check); a member at home gets no banner. It goes away by
 * itself when the floor leaves pre-alarm (armed, or kept off by a tag).
 */
export function FloorAlarmBannerView({ rows, networkStatus, dismissed, onDismiss }: FloorAlarmBannerViewProps) {
  const warnings = preAlarmWarnings(rows, networkStatus).filter((r) => !dismissed?.has(warningKey(r)));
  if (warnings.length === 0) return null;
  return (
    <Stack gap={0} role="region" aria-label="Alarm warning">
      {warnings.map((row) => (
        <Box
          key={row.floor_id}
          px="md"
          py="sm"
          role="alert"
          data-floor-alarm-warning={row.floor_id}
          style={{
            background: "var(--mantine-color-orange-light)",
            borderBottom: "2px solid var(--mantine-color-orange-filled)",
          }}
        >
          <Group gap="sm" wrap="nowrap" justify="space-between" align="center">
            <Group gap="sm" wrap="nowrap" miw={0}>
              <ThemeIcon color="orange" variant="filled" size={28} radius="xl" style={{ flexShrink: 0 }}>
                <AlertTriangle size={16} />
              </ThemeIcon>
              <Box>
                <Text fz="sm" fw={600} lh={1.35}>
                  {preAlarmWarningText(row)}
                </Text>
                {preAlarmWarningTime(row) && (
                  <Text fz="xs" c="dimmed" lh={1.2}>
                    {preAlarmWarningTime(row)}
                  </Text>
                )}
              </Box>
            </Group>
            <CloseButton aria-label="Hide alarm warning" onClick={() => onDismiss?.(warningKey(row))} />
          </Group>
        </Box>
      ))}
    </Stack>
  );
}

/** Mounted once in the app shell. */
export function FloorAlarmBanner() {
  const rows = useFloorAlarms();
  const { status } = useNetworkStatus();
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  return (
    <FloorAlarmBannerView
      rows={rows}
      networkStatus={status}
      dismissed={dismissed}
      onDismiss={(key) => setDismissed((prev) => new Set(prev).add(key))}
    />
  );
}
